import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AuditListRow, Store } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Card, Empty, ErrorNote, Input, Loading, PageHeader, Select } from '@/components/ui';
import { AuditStatusBadge, ModeBadge, ScoreBadge } from '@/components/badges';

export function AuditsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const f = {
    store_id: params.get('store_id') ?? '',
    mode: params.get('mode') ?? '',
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    archived: params.get('archived') ?? '',
  };
  const set = (k: keyof typeof f, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/api/stores') });
  const q = useQuery({ queryKey: ['audits', f], queryFn: () => api.get<AuditListRow[]>(`/api/audits${qs(f)}`) });

  return (
    <>
      <PageHeader title="Audits" subtitle="Every official and practice audit you can see." />
      <Card className="mb-4 grid gap-3 p-3 sm:grid-cols-3 lg:grid-cols-6">
        <Select value={f.store_id} onChange={(e) => set('store_id', e.target.value)}>
          <option value="">All stores</option>
          {stores.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <Select value={f.mode} onChange={(e) => set('mode', e.target.value)}>
          <option value="">Official & practice</option><option value="official">Official</option><option value="practice">Practice</option>
        </Select>
        <Select value={f.status} onChange={(e) => set('status', e.target.value)}>
          <option value="">Any status</option><option value="completed">Completed</option><option value="in_progress">In progress</option>
        </Select>
        <Input type="date" value={f.from} onChange={(e) => set('from', e.target.value)} aria-label="From" />
        <Input type="date" value={f.to} onChange={(e) => set('to', e.target.value)} aria-label="To" />
        {can('archive_audit') && (
          <Select value={f.archived} onChange={(e) => set('archived', e.target.value)}>
            <option value="">Active</option><option value="1">Archived</option>
          </Select>
        )}
      </Card>
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : q.data!.length === 0 ? (
        <Card><Empty icon={<ClipboardCheck className="size-8" />} title="No audits match." /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Date</th><th className="px-4 py-2 font-medium">Store</th><th className="px-4 py-2 font-medium">Type</th><th className="px-4 py-2 font-medium">Template</th><th className="px-4 py-2 font-medium">Auditor</th><th className="px-4 py-2 font-medium">Score</th><th className="px-4 py-2 font-medium">Status</th></tr></thead>
            <tbody>
              {q.data!.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-2.5 whitespace-nowrap"><Link to={`/audits/${a.id}`} className="font-medium hover:underline">{fmtDate(a.audit_date)}</Link></td>
                  <td className="px-4 py-2.5">{a.store_name} <span className="text-xs text-muted-fg">#{a.store_number}</span></td>
                  <td className="px-4 py-2.5"><ModeBadge mode={a.mode} /></td>
                  <td className="px-4 py-2.5">{a.template_name} <span className="text-xs text-muted-fg">v{a.version_no}</span></td>
                  <td className="px-4 py-2.5">{a.auditor_name}</td>
                  <td className="px-4 py-2.5">{a.status === 'completed' ? <ScoreBadge score={a.score} /> : '—'}</td>
                  <td className="px-4 py-2.5"><AuditStatusBadge status={a.status} locked={a.locked} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
